import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import AddBoxIcon from '@mui/icons-material/AddBox';
import SyncIcon from '@mui/icons-material/Sync';
import ArticleIcon from '@mui/icons-material/Article';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CampaignIcon from '@mui/icons-material/Campaign';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import { SyncStatusModal } from '~/shared/ui/modals/syncStatusModal/SyncStatusModal';
import {
  useCreateAccountMutation,
  useGetAccountsQuery,
  useLazyGetFilteredAccountsQuery,
  useSyncMutation,
} from '~/features/accounts/model/api/accounts-api';
import { ModalComment } from '~/shared/ui/modals/modalComment/ModalComment';
import { ModalAssign } from '~/shared/ui/modals/modalAssign/ModalAssign';
import Dashboard from '~/features/dashboard/Dashboard';
import { ModalTemplateCreateCampaign } from '~/shared/ui/modals/modalTemplateCreateCampaign/ModalTemplateCreateCampaign';
import { TemplateCampaignForm } from '~/shared/ui/modals/modalTemplateCreateCampaign/TemplateCampaignForm';
import { formatCustomer } from '~/shared/utils/formatCustomer';
import ModalAddAccount from '~/shared/ui/modals/modalAddAccount/ModalAddAccount';
import { CreateTemplateModal } from '~/shared/ui/modals/CreateTemplateModal';
import { DataTable } from '~/shared/ui/tables/DataTable';
import { IAccountRow, ICommentUser, IFormState, IModalState, IUserInfo } from '~/features/accounts/model/types/types';
import { defaultAccountsFilterColumns, defaultAccountsFormState, defaultAccountsModalState } from '~/shared/const/accountsDefaults';
import { IPaginationModel } from '~/shared/types/types';
import { defaultPaginationModel } from '~/shared/const/app';
import { hasAccess } from '~/shared/config/access';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { usersApi, useGetUsersQuery } from '~/features/user/model/api/users-api';

import { useNavigate } from 'react-router-dom';
import { usePersistedTableState } from '~/shared/hooks/usePersistedTableState';

export const AccountsPage: React.FC = () => {
  const { data: me } = useSelector(usersApi.endpoints.getUserInfo.select(undefined)) as { data: IUserInfo };
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { data: usersData } = useGetUsersQuery({
    page: 1,
    pageSize: 1000,
    search: null,
  });

  // Состояния формы и модальных окон
  const [formState, setFormState] = useState<IFormState>(defaultAccountsFormState);
  const [modalState, setModalState] = useState<IModalState>(defaultAccountsModalState);

  // Состояния фильтрации и выбора
  const [filterState, setFilterState] = useState<{ filteredAccounts: string[]; filteredRowsValue: string }>({
    filteredAccounts: [],
    filteredRowsValue: '',
  });

  const {
    activeFilters,
    setActiveFilters,
    sortModel,
    setSortModel,
    paginationModel,
    setPaginationModel,
  } = usePersistedTableState('accounts', defaultPaginationModel);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [allRowsMap, setAllRowsMap] = useState<Map<number, any>>(new Map()); // Added this line back
  const [commentUser, setCommentUser] = useState<ICommentUser>({ id: 0, name: '', comment: '' });
  const [commentPreview, setCommentPreview] = useState<{ open: boolean; name: string; comment: string }>({
    open: false,
    name: '',
    comment: '',
  });

  const sortBy = sortModel?.[0]?.field;
  const sortDir = sortModel?.[0]?.sort;

  const { data: responseData, error, isFetching, refetch } = useGetAccountsQuery({
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    search: paginationModel.search,
    sort_by: sortBy,
    sort_dir: sortDir,
  });

  const [getFilteredAccounts, { data: filteredData, isFetching: isFiltering }] = useLazyGetFilteredAccountsQuery();
  const filterColumns = useMemo(() => {
    const userOptions = [
      { label: 'Не назначен', value: '__none__' },
      ...(usersData?.data ?? []).map((u: any) => ({
        label: u?.name ?? `ID ${u?.id}`,
        value: String(u?.id),
      })),
    ];

    return defaultAccountsFilterColumns.map(col =>
      col.key === 'assigned_user_id'
        ? { ...col, options: userOptions }
        : col
    );
  }, [usersData?.data]);

  const rows = (activeFilters ? filteredData?.data : responseData?.data) || [];
  const rowCount = (activeFilters ? filteredData?.pagination?.total : responseData?.pagination?.total) || 0;

  useEffect(() => {
    if (activeFilters) {
      getFilteredAccounts({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: paginationModel.search,
        filters: activeFilters,
        sort_by: sortBy,
        sort_dir: sortDir,
      } as any);
    }
  }, [activeFilters, paginationModel, sortBy, sortDir]);

  const handleRefresh = useCallback(() => {
    if (activeFilters) {
      getFilteredAccounts({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: paginationModel.search,
        filters: activeFilters,
        sort_by: sortBy,
        sort_dir: sortDir,
      } as any);
      return;
    }
    refetch();
  }, [activeFilters, getFilteredAccounts, paginationModel.page, paginationModel.pageSize, paginationModel.search, refetch, sortBy, sortDir]);

  const [createAccount, { isLoading: isAccountCreating }] = useCreateAccountMutation();
  const [sync, { isLoading: isSyncLoading, data: syncData, error: syncError, reset: resetSync }] = useSyncMutation();
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);

  const handleSync = useCallback(() => {
    setIsSyncModalOpen(true);
    sync(undefined);
  }, [sync]);

  const handleCloseSyncModal = useCallback(() => {
    setIsSyncModalOpen(false);
    resetSync();
  }, [resetSync]);

  const handleModal = useCallback((key: keyof IModalState, value: boolean) => {
    setModalState(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleFormChange = useCallback((field: keyof IFormState, value: string) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleCustomerIdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, '');
      const formatted = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 10)].filter(Boolean).join('-');
      handleFormChange('customerIdMasked', formatted);
    },
    [handleFormChange]
  );

  const clearForm = useCallback(() => {
    setFormState(defaultAccountsFormState);
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      const customer_id = formState.customerIdMasked.replace(/-/g, '');
      await createAccount({
        customer_id,
        name: formState.name,
        link: formState.link,
        profileId: formState.profileId,
      }).unwrap();
      enqueueSnackbar('Аккаунт отправлен на создание', {
        variant: 'success',
        autoHideDuration: 3000,
        style: { maxWidth: '600px' },
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      clearForm();
      handleModal('openAddAccount', false);
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Ошибка при создании аккаунта', {
        variant: 'error',
        autoHideDuration: 3000,
        style: { maxWidth: '600px' },
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    }
  }, [createAccount, enqueueSnackbar, formState, clearForm, handleModal]);

  const isFormEmpty = useMemo(() => {
    return !formState.customerIdMasked || !formState.link || !formState.name;
  }, [formState]);

  const filteredRows = useMemo(() => {
    if (filterState.filteredAccounts.length === 0) return rows;
    return rows.filter(row => filterState.filteredAccounts.includes(row.name));
  }, [filterState.filteredAccounts, rows]);

  const selectedRows = useMemo(() => {
    return selectedIds.map(id => allRowsMap.get(id)).filter(Boolean);
  }, [selectedIds, allRowsMap]);

  const accountFields = [
    { label: 'ID клиента', field: 'customerIdMasked', handler: handleCustomerIdChange, placeholder: '___-___-____' },
    { label: 'Название', field: 'name', handler: (e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('name', e.target.value), placeholder: 'Название' },
    { label: 'Ссылка из авторизации', field: 'link', handler: (e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('link', e.target.value), placeholder: 'Ссылка' },
    { label: 'Profile ID', field: 'profileId', handler: (e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('profileId', e.target.value), placeholder: 'Profile ID' },
  ];

  const loadingText = useMemo(() => {
    if (paginationModel.search || activeFilters) {
      return 'идет поиск';
    }
    return 'загрузка данных';
  }, [paginationModel.search, activeFilters]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 70 },
      {
        field: 'name',
        headerName: 'Название',
        width: 270,
        minWidth: 270,
        flex: 0,
        renderCell: params => (
          <Tooltip onClick={(e) => e.stopPropagation()} title={params.value}><span>{params.value}</span></Tooltip>
        ),
      },
      {
        field: 'customer_id',
        headerName: 'ID клиента',
        width: 150,
        renderCell: params => (
          <Tooltip onClick={(e) => e.stopPropagation()} title={formatCustomer(params.value)}><span>{formatCustomer(params.value)}</span></Tooltip>
        ),
      },
      {
        field: 'currency_code',
        headerName: 'Валюта',
        width: 90,
        align: 'center',
        headerAlign: 'center',
        renderCell: params => (
          <Tooltip onClick={(e) => e.stopPropagation()} title={params.value || 'Не определена'}>
            <span>{params.value || '—'}</span>
          </Tooltip>
        ),
      },
      {
        field: 'assigned_user_name',
        headerName: 'Назначен',
        width: 200,
        sortable: true,
        renderCell: params => (
          <Tooltip onClick={(e) => e.stopPropagation()} title={params.value}><span>{params.value}</span></Tooltip>
        ),
      },
      {
        field: 'comment',
        headerName: 'Комментарий',
        sortable: false,
        width: 260,
        renderCell: params => {
          const comment = String(params.value ?? '');
          const isLong = comment.length > 60;
          const short = isLong ? `${comment.slice(0, 60)}…` : comment;

          return (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              onClick={(e) => e.stopPropagation()}
              sx={{ width: '100%', overflow: 'hidden' }}
            >
              <Tooltip title={comment}>
                <Typography
                  variant="body2"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}
                >
                  {short}
                </Typography>
              </Tooltip>
              {isLong ? (
                <Tooltip title="Подробнее">
                  <IconButton
                    size="small"
                    onClick={() => setCommentPreview({ open: true, name: params.row?.name ?? '', comment })}
                    sx={{ color: 'text.secondary', p: 0.5, m: 0.25 }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          );
        },
      },
      {
        field: 'is_launched',
        headerName: 'В работе',
        sortable: false,
        width: 110,
        align: 'center',
        headerAlign: 'center',
        renderCell: params => (
          <Tooltip title={params.value ? 'Идет создание кампании' : 'Аккаунт свободен'}>
            <span onClick={(e) => e.stopPropagation()} style={{ width: '100%', textAlign: 'center' }}>
              {params.value ? '🟢' : '⚪️'}
            </span>
          </Tooltip>
        ),
      },
      {
        field: 'successful_launch_count',
        headerName: 'Есть успешная РК',
        sortable: false,
        width: 170,
        align: 'center',
        headerAlign: 'center',
        renderCell: params => {
          const count = Number(params.value || 0);
          const has = count > 0;
          return (
            <Tooltip title={has ? `Успешных запусков: ${count}` : 'Успешных запусков нет'}>
              <span onClick={(e) => e.stopPropagation()} style={{ width: '100%', textAlign: 'center' }}>
                {has ? '✅' : '⛔️'}
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: 'created_at',
        headerName: 'Создан',
        width: 220,
        renderCell: (params) => (
          <span onClick={(e) => e.stopPropagation()}>
            {params.value
              ? new Date(params.value).toLocaleString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })
              : 'Не задано'}
          </span>
        ),
      },
      {
        field: 'edit',
        headerName: 'Действия',
        width: 110,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: params => (
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
            <Tooltip title="История создания РК">
              <IconButton
                size="small"
                sx={{ color: 'info.main', p: 0.5, m: 0.25 }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/launch-history/account/${params.row.id}`);
                }}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Редактировать комментарий">
              <IconButton
                size="small"
                sx={{ color: 'primary.main', p: 0.5, m: 0.25 }}
                onClick={(e) => {
                  e.stopPropagation()
                  setCommentUser({
                    id: params.row.id,
                    name: params.row.name,
                    comment: commentUser.comment,
                  });
                  handleModal('open', true);
                }}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [commentUser.comment, handleModal, navigate],
  );

  const onFilterApply = (filterState: Record<string, any>) => {
    const filters = {
      ...filterState,
      customer_id: filterState.customer_id?.replace(/\D/g, ''),
      date_from: filterState.date_from,
      date_to: filterState.date_to ? `${filterState.date_to} 23:59:59` : undefined,
      name: typeof filterState.name === 'string'
        ? filterState.name
          .split('\n')
          .map(s => s.trim())
          .filter(Boolean)
        : Array.isArray(filterState.name)
          ? filterState.name.filter(Boolean)
          : [],
    };

    // При смене фильтра сбрасываем выделение, чтобы чекбоксы
    // не оставались привязанными к старой, уже неактуальной выборке.
    setSelectedIds([]);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setActiveFilters(filters);
  };

  const onFilterReset = () => {
    // При полном сбросе фильтра также очищаем текущий выбор строк.
    setSelectedIds([]);
    setActiveFilters(null);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    // No need to call getFilteredAccounts explicitly if activeFilters is null, 
    // the component switches to responseData. 
    // However, we might want to ensure we are not showing stale filtered data while switching.
  };

  useEffect(() => {
    if (!rows?.length) return;
    setAllRowsMap(prev => {
      const map = new Map(prev);
      rows.forEach(row => map.set(row.id, row));
      return map;
    });
  }, [rows]);

  return (
    <Dashboard>
      {hasAccess(me?.roles ?? [], '/accounts') ? (
        <Stack flexDirection="column" gap="16px">

          {/* Заголовок и кнопка добавления */}
          <Stack direction={{ xs: 'column', lg: 'row' }}
            sx={{
              justifyContent: 'flex-start',
            }}>
            <Typography
              component="h2"
              variant="h6"
              sx={{
                display: 'flex',
                flex: 1,
                alignItems: 'center',
              }}
            >
              Аккаунты
            </Typography>

            <Stack direction="row" gap={2}>
              <ModalTemplateCreateCampaign
                open={modalState.openTemplateCreateCampaign}
                handleOpen={() => handleModal('openTemplateCreateCampaign', true)}
                handleClose={() =>
                  handleModal('openTemplateCreateCampaign', false)
                }
                disabled={selectedRows.length === 0}
                tootlipText={
                  selectedRows.length === 0
                    ? 'Для создания требуется выбрать аккаунты'
                    : 'Массовое создание кампаний'
                }
                title_button="Добавить кампании из шаблона"
                buttonIcon={<CampaignIcon />}
                minWidth="600px">
                <TemplateCampaignForm
                  selectedRows={selectedRows as IAccountRow[]}
                  onCancel={() =>
                    handleModal('openTemplateCreateCampaign', false)
                  }
                  onCreate={() => {
                    handleModal('openTemplateCreateCampaign', false);
                    handleRefresh();
                  }}
                />
              </ModalTemplateCreateCampaign>
              <Stack
                sx={{ flex: 1 }}
                flexDirection="row"
                gap={2}
                justifyContent="end">
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<ArticleIcon />}
                  onClick={() => handleModal('openTemplateCampaign', true)}>
                  Новый шаблон
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<SyncIcon />}
                  onClick={handleSync}>
                  Синхронизировать
                </Button>
                {me?.roles && !me.roles.includes('buyer') && !me.roles.includes('farmer') && (
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={selectedIds.length === 0}
                    startIcon={<AssignmentIndIcon />}
                    onClick={() => handleModal('openAssign', true)}>
                    Назначить
                  </Button>
                )}
                <ModalAssign
                  open={modalState.openAssign}
                  handleClose={() => handleModal('openAssign', false)}
                  ids={selectedRows}
                />
                <ModalAddAccount
                  isOpen={modalState.openAddAccount}
                  handleOpen={() => handleModal('openAddAccount', true)}
                  handleClose={() => {
                    handleModal('openAddAccount', false);
                    clearForm();
                  }}
                  title_button="Добавить аккаунт"
                  buttonIcon={<PersonAddAlt1Icon />}>
                  <Stack display="flex">
                    <Typography variant="h4" component="h4" pb={2}>
                      Создать аккаунт
                    </Typography>
                    <Stack gap="12px">
                      {accountFields.map(({ label, field, handler, placeholder }) => (
                        <Stack key={field} gap="8px">
                          <Typography variant="h6">{label}</Typography>
                          <TextField
                            required
                            placeholder={placeholder}
                            value={formState[field as keyof IFormState]}
                            onChange={handler}
                            error={field === 'customerIdMasked' && !formState.customerIdMasked.trim()}
                          />
                        </Stack>
                      ))}
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isFormEmpty || isAccountCreating}
                        sx={{ mt: 1 }}>
                        {isAccountCreating && (
                          <CircularProgress size={18} sx={{ mr: 1, color: 'white' }} />
                        )}
                        {isAccountCreating ? 'Привязка...' : 'Привязать'}
                      </Button>
                    </Stack>
                  </Stack>
                </ModalAddAccount>
              </Stack>
            </Stack>
          </Stack>

          <DataTable
            rows={filteredRows}
            columns={columns}
            rowCount={rowCount}
            isFetching={isFetching || isFiltering}
            error={error}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            sortModel={sortModel}
            setSortModel={(model: any) => {
              setSortModel(model);
              setPaginationModel(prev => ({ ...prev, page: 0 }));
            }}
            sortingMode="server"
            onFilterApply={onFilterApply as any}
            onFilterReset={onFilterReset as any}
            filterColumns={filterColumns}
            loadingText={loadingText}
            onRefresh={handleRefresh}
            activeFilters={activeFilters}
          />

          {/* Модальные окна */}
          <ModalComment
            open={modalState.open}
            handleClose={() => handleModal('open', false)}
            account={commentUser as any}
          />
          <Dialog
            open={commentPreview.open}
            onClose={() => setCommentPreview(prev => ({ ...prev, open: false }))}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Комментарий: {commentPreview.name}</DialogTitle>
            <DialogContent>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {commentPreview.comment}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCommentPreview(prev => ({ ...prev, open: false }))}>
                Закрыть
              </Button>
            </DialogActions>
          </Dialog>
          <CreateTemplateModal
            open={modalState.openTemplateCampaign}
            handleClose={() => handleModal('openTemplateCampaign', false)}
          />
          <SyncStatusModal
            open={isSyncModalOpen}
            onClose={handleCloseSyncModal}
            isLoading={isSyncLoading}
            data={syncData}
            error={syncError}
          />
        </Stack>
      ) : (
        <Stack>Нет доступа</Stack>
      )}
    </Dashboard>
  );
};
